// The review gate a multi-patient batch carries on the client (O64, R-S5-2,
// R-S5-51), kept OUT of the component so it can be driven directly by a test.
//
// O64 asks that "Confirm all reviewed cards" mean what it says: the provider
// has looked at every patient's card. So the turn remembers which cards were
// OPENED — expanded by the chevron, previewed under "Review & send", or edited
// — and the button stays disabled until every card still on it has been.
//
// Two separate facts, deliberately two separate maps:
//
//   turn.opened   the REVIEW record — append-only, never cleared, and held on
//                 EVERY turn of the batch (see `markOpenedInBatch`). It is what
//                 the gate reads, and what the page reports to the server as
//                 `openedSessionIds[]`.
//   turn.expanded the VIEW state — which cards are showing their orders right
//                 now. A whole-batch confirm collapses the ones that filed
//                 (their badge is the summary) and re-expands the ones that
//                 still need the provider (a card they must act on must not be
//                 hidden behind a chevron).
//
// Both live on the turn object beside `sentOrders`, so they are per-turn and
// UNPERSISTED: a reload re-arms the gate. That is the point — this is a review
// gate, not an authorisation gate. Confirming all grants nothing a card-by-card
// confirm does not (`/batch-confirm` is N ordinary confirms through the same
// primitive), so nothing here is a security property, and nothing here may
// become one. Deliberately: no storage write of any kind in this file.
//
// Module format: CommonJS (module.exports), like lib/order-batch.js — Jest
// reads this file directly and esbuild interops it for the bundle.

// The confirmable set is stated once, in lib/order-batch, because the gate's
// denominator and the server's `live.ready` must be the same idea (O78).
const { CONFIRMABLE_STATES } = require('./order-batch.js');

// The wording, once, for the button's hint AND for the server's refusal of a
// typed "confirm all" (O78). The engine requires this module for it, the way
// the other server/client shared rules are stated once (lib/payer-rules,
// lib/eligibility-infer): one sentence, so the button and the phrase can never
// tell the provider two different things.
const OPEN_EACH_CARD_HINT = 'Open each card to enable';

// A turn is a batch by the SAME test the reply is (lib/order-batch), never by
// how many orders or cards it happens to hold: the single-patient turn that
// staged two orders is not a batch and keeps every behaviour it has today.
function isBatchTurn(turn) {
  return !!(turn && turn.batchId != null && Array.isArray(turn.batch));
}

// The cards the gate is about: every sibling the ROSTER reports confirmable —
// not the cards this turn happens to be rendering.
//
// Those are different sets, and the difference was a real defect. The button
// confirms the whole BATCH by id (`/batch-confirm` walks every live sibling),
// but an older turn keeps only the cards it was drawn with: after a third
// patient joined, R-S5-47 refreshed that turn's roster to three and deliberately
// did not adopt her card, so its button still read "2 of 2 opened", enabled, and
// filed three orders — one of them for a patient nobody had read (codex, real
// PG: three creates). Meanwhile the typed phrase was refused, because the engine
// compares against `live.ready` — the two controls on one page disagreed, which
// is the one thing O78 exists to prevent. One denominator, the roster's.
//
// A turn drawn before the roster existed falls back to its own cards.
function confirmableSessionIds(turn) {
  if (!isBatchTurn(turn)) return [];
  const roster = Array.isArray(turn.sessions) ? turn.sessions : null;
  if (roster) {
    return roster
      .filter((s) => s && s.sessionId != null && CONFIRMABLE_STATES.has(s.state))
      .map((s) => String(s.sessionId));
  }
  return turn.batch.filter((e) => e && e.sessionId != null).map((e) => String(e.sessionId));
}

function openedFor(turn) {
  const map = (turn && turn.opened) || {};
  return new Set(Object.keys(map).filter((k) => map[k]));
}

function openedCount(turn) {
  const opened = openedFor(turn);
  return confirmableSessionIds(turn).filter((id) => opened.has(id)).length;
}

// Pure: returns a NEW turn, or the very same one when there is nothing to
// record — a single-patient turn is handed straight back, so the page's
// existing turns are untouched object-for-object with the flag off.
function markOpened(turn, sessionId) {
  if (!isBatchTurn(turn) || sessionId == null) return turn;
  const id = String(sessionId);
  if (turn.opened && turn.opened[id]) return turn;
  return { ...turn, opened: { ...(turn.opened || {}), [id]: true } };
}

// Every card on this turn has been opened. An empty batch opens nothing — the
// button is not rendered then anyway, and answering `true` would be a gate that
// passes when there is nothing to review.
function allOpened(turn) {
  const ids = confirmableSessionIds(turn);
  if (!ids.length) return false;
  const opened = openedFor(turn);
  return ids.every((id) => opened.has(id));
}

// What the page sends with a turn while a batch is live (O78). The ids the
// provider ACTUALLY opened — not the ones this turn happens to render — so a
// sibling that joined the batch after the cards were drawn is correctly NOT
// covered, and the typed phrase is refused until it too has been read.
function openedSessionIdsFor(turn) {
  return Array.from(openedFor(turn));
}

// The review is of the CARD — a session — not of the copy a later reply re-drew,
// so a card opened on any turn of the batch counts on every turn of it. That is
// also what makes the button and `openedSessionIds[]` ONE rule: the page reports
// the newest batch turn's set, and every turn holds the same union, so the two
// can never disagree (O78). Per-turn storage would instead leave every older
// turn permanently disabled while the newest re-demanded gestures the provider
// had already made — friction without safety. Still per page load: nothing is
// persisted and a reload re-arms all of it (R-S5-51).
function markOpenedInBatch(turns, batchId, sessionId) {
  const list = Array.isArray(turns) ? turns : [];
  if (batchId == null || sessionId == null) return list;
  return list.map((t) => (t && t.batchId != null && String(t.batchId) === String(batchId)
    ? markOpened(t, sessionId)
    : t));
}

// …and a turn BORN later inherits it. A gesture propagates across the turns on
// screen, but the next reply draws a fresh turn with no record of its own, and
// that turn is the one the page reports `openedSessionIds[]` from — so without
// this the union would reset every time the assistant answered. Applied wherever
// turns are installed; idempotent, and it hands back the very same array (and
// the very same turn objects) when there is nothing to carry.
function carryOpenedForward(turns) {
  const list = Array.isArray(turns) ? turns : [];
  const byBatch = new Map();
  for (const t of list) {
    if (!t || t.batchId == null) continue;
    const key = String(t.batchId);
    const acc = byBatch.get(key) || {};
    for (const id of Object.keys(t.opened || {})) if (t.opened[id]) acc[id] = true;
    byBatch.set(key, acc);
  }
  if (!byBatch.size) return list;
  let changed = false;
  const out = list.map((t) => {
    if (!t || t.batchId == null) return t;
    const union = byBatch.get(String(t.batchId)) || {};
    const ids = Object.keys(union);
    if (!ids.length) return t;
    const cur = t.opened || {};
    if (ids.every((id) => cur[id])) return t;
    changed = true;
    return { ...t, opened: { ...cur, ...union } };
  });
  return changed ? out : list;
}

// Cards start COLLAPSED. Not even a batch that has shrunk to one card opens
// itself: an auto-open is a gate that disappears in the commonest tail state.
function isExpanded(turn, sessionId) {
  const map = (turn && turn.expanded) || {};
  return !!map[String(sessionId)];
}

function toggleExpanded(turn, sessionId) {
  if (!isBatchTurn(turn) || sessionId == null) return turn;
  const id = String(sessionId);
  const next = !isExpanded(turn, id);
  const out = { ...turn, expanded: { ...(turn.expanded || {}), [id]: next } };
  // Opening it is one of the three gestures that count as a review; closing it
  // again does not take that back.
  return next ? markOpened(out, id) : out;
}

// After a whole-batch confirm: what filed collapses behind its badge, and what
// still needs the provider is expanded whatever they had done with the chevron.
// It reads the outcome the results were already folded into (lib/order-batch),
// so "what happened to this card" is decided once.
function collapseFinished(turn) {
  if (!isBatchTurn(turn)) return turn;
  const bySession = (turn.outcomes && turn.outcomes.bySession) || null;
  if (!bySession) return turn;
  const expanded = { ...(turn.expanded || {}) };
  let changed = false;
  for (const id of Object.keys(bySession)) {
    const next = bySession[id].kind !== 'filed';
    if (expanded[id] !== next) { expanded[id] = next; changed = true; }
  }
  return changed ? { ...turn, expanded } : turn;
}

module.exports = {
  OPEN_EACH_CARD_HINT,
  isBatchTurn, confirmableSessionIds,
  openedFor, openedCount, markOpened, markOpenedInBatch, carryOpenedForward, allOpened, openedSessionIdsFor,
  isExpanded, toggleExpanded, collapseFinished,
};
